/**
 * CSV parsing and processing utilities
 */

import Papa from "papaparse";

import type {
  CSVPreviewData,
  AutoMappingResult,
  ContactField,
} from "../types/csv";

export function parseCSV(
  text: string,
  previewRowCount: number = 3,
): CSVPreviewData {
  const result = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: true,
  });

  const rows = result.data;
  if (rows.length === 0) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const headers = rows[0].map((h) => h.trim());
  const previewRows = rows.slice(1, previewRowCount + 1);
  const totalRows = rows.length - 1;

  return { headers, rows: previewRows, totalRows };
}

export function coerceToE164(value: string): string {
  const cleaned = value.replace(/[\s\-().]/g, "");
  if (!cleaned) return value;
  const digits = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;
  if (!/^\d+$/.test(digits)) return value;
  return cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
}

export interface SyntheticColumn {
  header: string;
  target: "email_consented" | "sms_consented" | "email_suppressed";
  value: "true" | "false";
}

export function appendSyntheticColumns(
  text: string,
  columns: SyntheticColumn[],
): string {
  if (columns.length === 0) return text;
  const parsed = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: true,
  });
  const rows = parsed.data;
  if (rows.length === 0) return text;

  rows[0] = [...rows[0], ...columns.map((c) => c.header)];
  for (let i = 1; i < rows.length; i++) {
    rows[i] = [...rows[i], ...columns.map((c) => c.value)];
  }

  return Papa.unparse(rows, { newline: "\n" });
}

export function cleanCSV(text: string, phoneColumnIndex?: number): string {
  const parsed = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: false,
  });

  const rows = parsed.data;
  if (rows.length < 2) return text;

  if (phoneColumnIndex != null && phoneColumnIndex >= 0) {
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (phoneColumnIndex < row.length && row[phoneColumnIndex].trim()) {
        row[phoneColumnIndex] = coerceToE164(row[phoneColumnIndex]);
      }
    }
  }

  return Papa.unparse(rows, { newline: "\n" });
}

/**
 * Normalizes a header string for field matching
 */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

/**
 * Common field name variations for auto-mapping
 */
const FIELD_VARIATIONS: Record<string, string[]> = {
  first_name: ["firstname", "fname"],
  last_name: ["lastname", "lname"],
  email: ["emailaddress", "email_address"],
  phone: ["phonenumber", "phone_number"],
  postal_code: ["zipcode", "zip", "zip_code"],
  address1: ["address", "street", "street_address"],
  address2: ["address_line_2", "address2", "apt", "suite"],
  email_consented: [
    "subscribed",
    "email_subscribed",
    "is_subscribed",
    "consent",
    "consented",
    "email_consent",
    "opt_in",
    "email_opt_in",
  ],
  email_consented_updated_at: [
    "opt_in_date",
    "email_opt_in_date",
    "consent_date",
    "email_consent_date",
    "subscribed_at",
    "email_subscribed_at",
    "consent_updated_at",
    "email_consent_updated_at",
    "consent_timestamp",
    "date_subscribed",
  ],
  email_suppressed: [
    "suppressed",
    "email_suppression",
    "is_suppressed",
    "email_blocked",
    "do_not_email",
  ],
  sms_consented: [
    "sms_subscribed",
    "sms_consent",
    "sms_opt_in",
    "phone_consent",
    "phone_opt_in",
  ],
  sms_consented_updated_at: [
    "sms_opt_in_date",
    "sms_consent_date",
    "sms_subscribed_at",
    "phone_consent_date",
    "phone_opt_in_date",
    "sms_consent_updated_at",
  ],
  tags: ["tag", "labels", "label"],
};

/**
 * Match quality for field mapping - higher is better
 */
enum MatchQuality {
  NONE = 0,
  VARIATION = 1,
  EXACT = 2,
}

/**
 * Creates automatic field mappings based on CSV headers
 */
export function createAutoMapping(
  headers: string[],
  contactFields: ContactField[],
): AutoMappingResult {
  const globalFields = contactFields
    .filter((f) => !f.isListSpecific)
    .map((f) => f.value);
  const listSpecificFields = contactFields
    .filter((f) => f.isListSpecific)
    .map((f) => f.value);

  const mappings: { [key: string]: number } = {};
  const listSpecificMappings: { [key: string]: number } = {};
  const autoCheckedColumns: { [key: string]: boolean } = {};
  const columnMappingsUI: { [key: string]: string } = {};

  const globalFieldMatchQuality: { [key: string]: MatchQuality } = {};
  const listSpecificFieldMatchQuality: { [key: string]: MatchQuality } = {};

  headers.forEach((header, index) => {
    const normalizedHeader = normalizeHeader(header);

    if (globalFields.includes(normalizedHeader)) {
      if (
        !globalFieldMatchQuality[normalizedHeader] ||
        globalFieldMatchQuality[normalizedHeader] < MatchQuality.EXACT
      ) {
        if (mappings[normalizedHeader] !== undefined) {
          const prevHeader = headers[mappings[normalizedHeader]];
          delete autoCheckedColumns[prevHeader];
          delete columnMappingsUI[prevHeader];
        }

        mappings[normalizedHeader] = index;
        autoCheckedColumns[header] = true;
        columnMappingsUI[header] = normalizedHeader;
        globalFieldMatchQuality[normalizedHeader] = MatchQuality.EXACT;
      }
      return;
    }

    if (listSpecificFields.includes(normalizedHeader)) {
      if (
        !listSpecificFieldMatchQuality[normalizedHeader] ||
        listSpecificFieldMatchQuality[normalizedHeader] < MatchQuality.EXACT
      ) {
        if (listSpecificMappings[normalizedHeader] !== undefined) {
          const prevHeader = headers[listSpecificMappings[normalizedHeader]];
          delete autoCheckedColumns[prevHeader];
          delete columnMappingsUI[prevHeader];
        }

        listSpecificMappings[normalizedHeader] = index;
        autoCheckedColumns[header] = true;
        columnMappingsUI[header] = normalizedHeader;
        listSpecificFieldMatchQuality[normalizedHeader] = MatchQuality.EXACT;
      }
      return;
    }

    let matched = false;
    for (const [field, variations] of Object.entries(FIELD_VARIATIONS)) {
      if (variations.includes(normalizedHeader)) {
        if (
          !globalFieldMatchQuality[field] ||
          globalFieldMatchQuality[field] < MatchQuality.VARIATION
        ) {
          if (mappings[field] !== undefined) {
            const prevHeader = headers[mappings[field]];
            delete autoCheckedColumns[prevHeader];
            delete columnMappingsUI[prevHeader];
          }

          mappings[field] = index;
          autoCheckedColumns[header] = true;
          columnMappingsUI[header] = field;
          globalFieldMatchQuality[field] = MatchQuality.VARIATION;
        }
        matched = true;
        break;
      }
    }

    if (!matched) {
      autoCheckedColumns[header] = false;
    }
  });

  // Aliases like `opt_in_date` read as email consent, so a phone-only CSV
  // would otherwise auto-map an email consent timestamp with no email to
  // attach it to. Leave those columns unmapped for the user to map by hand.
  for (const [timestampField, identifierField] of [
    ["email_consented_updated_at", "email"],
    ["sms_consented_updated_at", "phone"],
  ]) {
    if (
      mappings[timestampField] === undefined ||
      mappings[identifierField] !== undefined
    ) {
      continue;
    }
    const header = headers[mappings[timestampField]];
    delete mappings[timestampField];
    delete columnMappingsUI[header];
    autoCheckedColumns[header] = false;
  }

  return {
    mappings,
    listSpecificMappings,
    autoCheckedColumns,
    columnMappingsUI,
  };
}

/**
 * Converts text to a File object for CSV processing
 */
export function textToCSVFile(
  text: string,
  filename: string = "pasted-data.csv",
): File {
  const blob = new Blob([text], { type: "text/csv" });
  return new File([blob], filename, { type: "text/csv" });
}

/**
 * Validates if a file is a CSV file
 */
export function isValidCSVFile(file: File): boolean {
  return file.type === "text/csv" || file.name.endsWith(".csv");
}

/**
 * Gets file size in a human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
